import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { usePublicTenant } from './usePublicTenant';

export interface PublicSiteSettings {
  company_logo_url: string;
  favicon_url: string;
  admin_logo_url: string;
  payment_visa_url: string;
  payment_mastercard_url: string;
  payment_amex_url: string;
  payment_klarna_url: string;
}

const defaults: PublicSiteSettings = {
  company_logo_url: '',
  favicon_url: '',
  admin_logo_url: '',
  payment_visa_url: '',
  payment_mastercard_url: '',
  payment_amex_url: '',
  payment_klarna_url: '',
};

const CACHE_DURATION = 5 * 60 * 1000;
const cacheByTenant: Map<string, { data: PublicSiteSettings; ts: number }> = new Map();

function cacheKey(tenantId: string | null): string {
  return tenantId ?? '__default__';
}

export function usePublicSiteSettings() {
  const { tenantId } = usePublicTenant();
  const [settings, setSettings] = useState<PublicSiteSettings>(defaults);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const key = cacheKey(tenantId);
      const now = Date.now();
      const hit = cacheByTenant.get(key);
      if (hit && now - hit.ts < CACHE_DURATION) {
        if (!cancelled) setSettings(hit.data);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const { data, error: rpcError } = await supabase.rpc('get_public_site_settings', {
          p_tenant_id: tenantId,
        });
        if (cancelled) return;
        if (rpcError) {
          setError(rpcError);
          setSettings(defaults);
          return;
        }
        const normalized: PublicSiteSettings = { ...defaults, ...(data || {}) };
        cacheByTenant.set(key, { data: normalized, ts: Date.now() });
        setSettings(normalized);
      } catch (e) {
        if (!cancelled) {
          setError(e as Error);
          setSettings(defaults);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [tenantId]);

  const refreshSettings = async () => {
    const key = cacheByTenant.get(cacheKey(tenantId)) ? cacheKey(tenantId) : null;
    if (key) cacheByTenant.delete(key);
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('get_public_site_settings', {
        p_tenant_id: tenantId,
      });
      if (rpcError) throw rpcError;
      const normalized: PublicSiteSettings = { ...defaults, ...(data || {}) };
      cacheByTenant.set(cacheKey(tenantId), { data: normalized, ts: Date.now() });
      setSettings(normalized);
    } catch (e) {
      setError(e as Error);
      setSettings(defaults);
    } finally {
      setLoading(false);
    }
  };

  return { settings, loading, error, refreshSettings };
}

